/**
 * A projector never benefits from keeping a 12k+ source raster resident just
 * to paint a 4k (or smaller) display.  Very large VTT exports otherwise make
 * the browser retain several hundred MiB per open DM / Player Display texture.
 * These limits retain crisp 4k playback while keeping the decoded texture well
 * below the memory cliff on integrated GPUs.
 */
export const MAP_RENDER_MAX_DIMENSION = 4_096;
export const MAP_RENDER_MAX_PIXELS = 24_000_000;

export function mapRasterDimensions(
  width: number,
  height: number,
  maxDimension = MAP_RENDER_MAX_DIMENSION,
  maxPixels = MAP_RENDER_MAX_PIXELS,
) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error("The map image has invalid dimensions.");
  }
  const scale = Math.min(1, maxDimension / width, maxDimension / height, Math.sqrt(maxPixels / (width * height)));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)), changed: scale < .9999 };
}

function canvasBlob(canvas: OffscreenCanvas | HTMLCanvasElement, type: string) {
  if ("convertToBlob" in canvas) return canvas.convertToBlob({ type });
  return new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob((blob) => blob ? resolve(blob) : reject(new Error("The browser could not encode the optimized map image.")), type);
  });
}

/** Create a display-safe derivative without mutating the user's original file. */
export async function prepareMapRaster(source: Blob, knownDimensions?: { width?: number; height?: number }) {
  if (!source.type.startsWith("image/")) return source;
  const hinted = knownDimensions?.width && knownDimensions?.height
    ? mapRasterDimensions(knownDimensions.width, knownDimensions.height)
    : undefined;
  // Chromium performs the resize while decoding. That prevents a 12k source
  // from ever becoming a full-size compositor texture just to make a 4k copy.
  let original: ImageBitmap;
  if (hinted?.changed) {
    try { original = await createImageBitmap(source, { resizeWidth: hinted.width, resizeHeight: hinted.height, resizeQuality: "high" }); }
    catch { original = await createImageBitmap(source); }
  } else original = await createImageBitmap(source);
  const dimensions = hinted ?? mapRasterDimensions(original.width, original.height);
  if (!dimensions.changed && original.width === dimensions.width && original.height === dimensions.height) { original.close(); return source; }

  const canvas: OffscreenCanvas | HTMLCanvasElement = typeof OffscreenCanvas === "function"
    ? new OffscreenCanvas(dimensions.width, dimensions.height)
    : Object.assign(document.createElement("canvas"), { width: dimensions.width, height: dimensions.height });
  const context = canvas.getContext("2d", { alpha: source.type !== "image/jpeg" }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!context) { original.close(); throw new Error("The browser could not create a map optimization canvas."); }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(original, 0, 0, dimensions.width, dimensions.height);
  original.close();
  return canvasBlob(canvas, source.type === "image/jpeg" ? "image/jpeg" : "image/png");
}
